/**
 * Chat Panel Component
 *
 * Core chat UI for engaging with a user's representation agent.
 * Used directly in PublicProfile (embedded) and wrapped by ChatWidget (modal).
 */

import { useState, useRef, useEffect } from 'preact/hooks';

const API_BASE = import.meta.env.VITE_API_URL || 'https://pxtxt.itsmin.workers.dev';

/** Render inline markdown (bold, italic) to HTML. Safe for controlled LLM output. */
function renderInlineMarkdown(text: string): string {
	return text
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
		.replace(/\*(.+?)\*/g, '<em>$1</em>');
}

interface Message {
	role: 'user' | 'assistant';
	content: string;
	timestamp: number;
}

export interface ChatPanelProps {
	slug: string;
	displayName: string;
	profileContext?: {
		workHistory?: Array<{ company: string; title?: string }>;
		focusAreas?: string[];
	};
	onFocus?: (companyName: string) => void;
	embedded?: boolean;
}

export function ChatPanel({ slug, displayName, profileContext, onFocus, embedded = false }: ChatPanelProps) {
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [engagementId, setEngagementId] = useState<string | null>(null);

	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	const firstName = displayName.split(' ')[0];

	function autoResizeTextarea() {
		const textarea = inputRef.current;
		if (textarea) {
			textarea.style.height = 'auto';
			textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	}

	async function sendMessage(overrideMessage?: string) {
		const userMessage = (overrideMessage || input).trim();
		if (!userMessage || sending) return;

		setInput('');
		setSending(true);
		setError(null);

		if (inputRef.current) {
			inputRef.current.style.height = 'auto';
		}

		const optimisticMessage: Message = {
			role: 'user',
			content: userMessage,
			timestamp: Date.now(),
		};
		setMessages(prev => [...prev, optimisticMessage]);

		try {
			const response = await fetch(`${API_BASE}/api/public/engage/${slug}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					message: userMessage,
					engagementId,
					context: { currentNode: 'profile' },
				}),
			});

			if (!response.ok) {
				throw new Error('Failed to send message');
			}

			const data = await response.json();

			if (data.engagementId) {
				setEngagementId(data.engagementId);
			}

			// Handle focus signal from agent
			if (data.focus && onFocus) {
				onFocus(data.focus);
			}

			const assistantMessage: Message = {
				role: 'assistant',
				content: data.message,
				timestamp: Date.now(),
			};
			setMessages(prev => [...prev, assistantMessage]);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to send message');
			setMessages(prev => prev.filter(m => m !== optimisticMessage));
		} finally {
			setSending(false);
		}
	}

	// Compute suggested prompts from profile context
	function getSuggestedPrompts(): Array<{ label: string; message: string }> {
		const prompts: Array<{ label: string; message: string }> = [
			{ label: `What should I know about ${firstName}?`, message: `What should I know about ${firstName}?` },
		];

		if (profileContext?.workHistory?.[0]) {
			prompts.push({
				label: `${firstName}'s work at ${profileContext.workHistory[0].company}`,
				message: `Tell me about ${firstName}'s work at ${profileContext.workHistory[0].company}`,
			});
		}

		if (profileContext?.focusAreas?.[0]) {
			prompts.push({
				label: `How ${firstName} approaches ${profileContext.focusAreas[0].toLowerCase()}`,
				message: `How does ${firstName} approach ${profileContext.focusAreas[0].toLowerCase()}?`,
			});
		} else if (profileContext?.workHistory?.[1]) {
			prompts.push({
				label: `${firstName}'s work at ${profileContext.workHistory[1].company}`,
				message: `Tell me about ${firstName}'s work at ${profileContext.workHistory[1].company}`,
			});
		}

		return prompts;
	}

	const containerClass = `chat-panel ${embedded ? 'chat-panel--embedded' : ''}`;

	return (
		<div class={containerClass}>
			{/* Header */}
			<div class="chat-panel-header">
				<div class="chat-panel-header-info">
					<div class="chat-panel-avatar">
						{displayName.charAt(0).toUpperCase()}
					</div>
					<div>
						<h3 class="chat-panel-title">{firstName}'s Agent</h3>
						<p class="chat-panel-subtitle">Professional representation</p>
					</div>
				</div>
			</div>

			{/* Messages */}
			<div class="chat-panel-messages">
				{messages.length === 0 ? (
					<div class="chat-panel-welcome">
						<p>Ask me about {firstName}'s work, experience, or approach.</p>
						<div class="chat-panel-prompts">
							{getSuggestedPrompts().map((prompt, i) => (
								<button
									key={i}
									class="chat-panel-prompt"
									onClick={() => sendMessage(prompt.message)}
								>
									{prompt.label}
								</button>
							))}
						</div>
					</div>
				) : (
					<>
						{messages.map((msg, i) => (
							<div key={i} class={`chat-panel-message chat-panel-message--${msg.role}`}>
								{msg.role === 'assistant' && (
									<div class="chat-panel-message-avatar">
										<img src="/pxtxt-avatar.svg" alt="" width="20" height="20" />
									</div>
								)}
								<div class="chat-panel-message-content">
									{msg.content.split('\n').map((line, j) => (
										<p key={j} dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(line) }} />
									))}
								</div>
							</div>
						))}
						{sending && (
							<div class="chat-panel-message chat-panel-message--assistant">
								<div class="chat-panel-message-avatar">
									<img src="/pxtxt-avatar.svg" alt="" width="20" height="20" />
								</div>
								<div class="chat-panel-message-content chat-panel-typing">
									<span></span><span></span><span></span>
								</div>
							</div>
						)}
					</>
				)}
				<div ref={messagesEndRef} />
			</div>

			{/* Error */}
			{error && (
				<div class="chat-panel-error">
					<span>{error}</span>
					<button onClick={() => setError(null)}>
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
							<path d="M18 6L6 18M6 6l12 12" />
						</svg>
					</button>
				</div>
			)}

			{/* Input */}
			<div class="chat-panel-input">
				<textarea
					ref={inputRef}
					class="chat-panel-input-field"
					value={input}
					onInput={(e) => {
						setInput((e.target as HTMLTextAreaElement).value);
						autoResizeTextarea();
					}}
					onKeyDown={handleKeyDown}
					placeholder={`Ask about ${firstName}'s experience...`}
					disabled={sending}
					rows={1}
				/>
				<button
					class="chat-panel-send"
					disabled={!input.trim() || sending}
					onClick={() => sendMessage()}
				>
					<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
					</svg>
				</button>
			</div>

			<style>{chatPanelStyles}</style>
		</div>
	);
}

const chatPanelStyles = `
	.chat-panel {
		display: flex;
		flex-direction: column;
		height: 100%;
		background: var(--color-bg-elevated);
		border-radius: var(--radius-lg);
		overflow: hidden;
	}

	.chat-panel--embedded {
		border-radius: 0;
		border-left: 1px solid var(--color-border);
	}

	.chat-panel-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1rem;
		border-bottom: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		flex-shrink: 0;
	}

	.chat-panel-header-info {
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.chat-panel-avatar {
		width: 36px;
		height: 36px;
		border-radius: 50%;
		background: var(--color-primary);
		color: white;
		display: flex;
		align-items: center;
		justify-content: center;
		font-family: var(--font-display);
		font-weight: 700;
		font-size: 1rem;
	}

	.chat-panel-title {
		font-family: var(--font-display);
		font-size: 0.9375rem;
		font-weight: 600;
		color: var(--color-title);
		margin: 0;
	}

	.chat-panel-subtitle {
		font-size: 0.6875rem;
		color: var(--color-text-muted);
		margin: 0;
	}

	.chat-panel-messages {
		flex: 1;
		overflow-y: auto;
		padding: 1rem;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;
	}

	.chat-panel-welcome {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		flex: 1;
		text-align: center;
		padding: 1rem;
		color: var(--color-text-muted);
		font-size: 0.9375rem;
		line-height: 1.5;
	}

	.chat-panel-welcome p {
		margin: 0 0 1.25rem;
	}

	.chat-panel-prompts {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		width: 100%;
	}

	.chat-panel-prompt {
		padding: 0.625rem 0.875rem;
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: var(--radius-md);
		font-size: 0.8125rem;
		color: var(--color-text);
		cursor: pointer;
		text-align: left;
		transition: background 150ms, border-color 150ms;
	}

	.chat-panel-prompt:hover {
		background: var(--color-bg-elevated);
		border-color: var(--color-primary);
	}

	.chat-panel-message {
		display: flex;
		gap: 4px;
		max-width: 90%;
		align-items: flex-end;
		animation: chat-panel-slide-up 200ms var(--ease-out-expo);
	}

	.chat-panel-message--user {
		align-self: flex-end;
		flex-direction: row-reverse;
	}

	.chat-panel-message--assistant {
		align-self: flex-start;
	}

	.chat-panel-message-avatar {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
	}

	.chat-panel-message-avatar img {
		display: block;
		width: 20px;
		height: 20px;
	}

	.chat-panel-message-content {
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 12px;
		padding: 0.5rem 0.75rem;
		font-size: 0.8125rem;
		line-height: 1.5;
	}

	.chat-panel-message-content p {
		margin: 0;
	}

	.chat-panel-message-content p + p {
		margin-top: 0.25rem;
	}

	.chat-panel-message--user .chat-panel-message-content {
		background: var(--color-primary);
		border-color: var(--color-primary);
		color: white;
		border-bottom-right-radius: 4px;
	}

	.chat-panel-message--assistant .chat-panel-message-content {
		border-bottom-left-radius: 4px;
	}

	.chat-panel-typing {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 0.625rem 0.75rem;
	}

	.chat-panel-typing span {
		width: 5px;
		height: 5px;
		background: var(--color-text-muted);
		border-radius: 50%;
		animation: chat-panel-typing-dot 1.4s infinite;
	}

	.chat-panel-typing span:nth-child(2) { animation-delay: 0.2s; }
	.chat-panel-typing span:nth-child(3) { animation-delay: 0.4s; }

	@keyframes chat-panel-typing-dot {
		0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
		30% { opacity: 1; transform: translateY(-2px); }
	}

	.chat-panel-error {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.5rem 0.75rem;
		margin: 0 1rem;
		background: rgba(222, 46, 33, 0.1);
		border: 1px solid var(--color-error);
		border-radius: var(--radius-md);
		color: var(--color-error);
		font-size: 0.75rem;
		flex-shrink: 0;
	}

	.chat-panel-error button {
		background: none;
		border: none;
		color: inherit;
		cursor: pointer;
		padding: 2px;
		display: flex;
		opacity: 0.7;
	}

	.chat-panel-error button:hover { opacity: 1; }

	.chat-panel-input {
		display: flex;
		align-items: flex-end;
		gap: 0.5rem;
		padding: 0.75rem 1rem;
		border-top: 1px solid var(--color-border);
		background: var(--color-bg-elevated);
		flex-shrink: 0;
	}

	.chat-panel-input-field {
		flex: 1;
		padding: 0.5rem 0;
		background: transparent;
		border: none;
		font-family: var(--font-body);
		font-size: 0.8125rem;
		color: var(--color-text);
		outline: none;
		resize: none;
		min-height: 1.25em;
		max-height: 120px;
		line-height: 1.5;
	}

	.chat-panel-input-field::placeholder {
		color: var(--color-text-muted);
	}

	.chat-panel-input-field:disabled {
		opacity: 0.5;
	}

	.chat-panel-send {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 2rem;
		height: 2rem;
		padding: 0;
		background: var(--color-primary);
		border: none;
		border-radius: var(--radius-md);
		color: white;
		cursor: pointer;
		transition: background 150ms, transform 150ms;
		flex-shrink: 0;
	}

	.chat-panel-send:hover:not(:disabled) {
		background: var(--color-primary-hover);
		transform: scale(1.02);
	}

	.chat-panel-send:disabled {
		background: var(--color-text-muted);
		cursor: not-allowed;
	}

	@keyframes chat-panel-slide-up {
		from { opacity: 0; transform: translateY(8px); }
		to { opacity: 1; transform: translateY(0); }
	}
`;
